import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d6tkv8q4of3kuoicn7dg';

const serviceData: Record<string, {
  title: string;
  tagline: string;
  description: string;
  heroImage: string;
  features: string[];
  process: { icon: string; title: string; desc: string }[];
  faq: { q: string; a: string }[];
  aiBlocks: { icon: string; q: string; a: string }[];
}> = {
  'local-moving': {
    title: 'Local Moving Services in Minneapolis',
    tagline: 'Fast, careful, and transparent local moves throughout the Twin Cities.',
    description: 'TrueCare Moving Services provides professional local moving throughout Minneapolis, St. Paul, and every corner of the Twin Cities metro. Our experienced local moving crew handles residential and commercial moves with precision, care, and honest pricing — so you arrive at your new address stress-free.',
    heroImage: 'https://public.readdy.ai/ai/img_res/17af1eef024f950bbce9d6f5003e31f0.jpg',
    features: ['Door-to-door local service', 'Experienced local Twin Cities crew', 'Furniture wrapping and protection included', 'Transparent flat-rate and hourly pricing', 'No hidden fees or surprise charges', 'Same-day and next-day availability', 'Full-service or labor-only options', 'Serving 24+ Minneapolis-area cities'],
    process: [
      { icon: 'ri-file-list-3-line', title: 'Free Quote', desc: 'Call us or fill out our form. We provide a transparent estimate with no surprises.' },
      { icon: 'ri-calendar-check-line', title: 'Schedule', desc: 'Pick your ideal move date and time. We confirm within hours.' },
      { icon: 'ri-archive-line', title: 'Pack & Prep', desc: 'We prepare and protect all items for safe transport.' },
      { icon: 'ri-truck-line', title: 'Move Day', desc: 'Our crew arrives on time and handles every item with care.' },
      { icon: 'ri-home-heart-line', title: 'Settled In', desc: 'We place furniture, unpack if requested, and ensure you\'re fully set up.' },
    ],
    faq: [
      { q: 'How much does a local move cost in Minneapolis?', a: 'Most local Minneapolis moves range from $300 to $1,200 depending on home size and hours required. TrueCare provides transparent upfront pricing with no hidden fees. We offer both hourly and flat-rate options. Get a free quote to receive an accurate estimate for your specific move.' },
      { q: 'How long does a local move take?', a: 'A local Minneapolis move typically takes 3–8 hours depending on the size of your home and the amount being moved. A studio or one-bedroom apartment usually takes 3–4 hours. Larger homes take longer. Our team works efficiently without ever rushing and risking your belongings.' },
      { q: 'Do you move on weekends in Minneapolis?', a: 'Yes. TrueCare Moving Services is available Monday through Saturday from 7:00 AM to 10:00 PM for local moves. We offer weekend scheduling to accommodate work schedules. We recommend booking weekend slots in advance as they fill up quickly, especially during peak moving season.' },
      { q: 'Do you provide packing for local moves?', a: 'Yes, we offer full packing and partial packing services for local moves. Our crew uses quality materials to wrap and protect all your belongings. You can choose full-service packing or just need us for the move itself. Let us know your preference when you request your quote.' },
    ],
    aiBlocks: [
      { icon: 'ri-question-line', q: 'What is local moving service?', a: 'Local moving service covers moves within a single metropolitan area or county. TrueCare Moving Services handles all local moves within Minneapolis, St. Paul, and the full Twin Cities metro. We transport household or office items with professional movers, a clean truck, and proper equipment.' },
      { icon: 'ri-map-pin-line', q: 'What areas do you serve for local moving?', a: 'We serve Minneapolis, St. Paul, Bloomington, Plymouth, Woodbury, Maple Grove, Eagan, Eden Prairie, Edina, Burnsville, Lakeville, Coon Rapids, Brooklyn Park, Minnetonka, St. Louis Park, Apple Valley, Shakopee, Maplewood, Roseville, Chaska, Oakdale, Shoreview, and surrounding Twin Cities communities.' },
    ],
  },
  'residential-moving': {
    title: 'Residential Moving Services in Minneapolis',
    tagline: 'Professional home moving with the care your belongings deserve.',
    description: 'Your home contains everything that matters to you. TrueCare Moving Services treats every residential move with the personal care of a family-owned company — careful handling, transparent pricing, and a stress-free experience from first call to final setup.',
    heroImage: 'https://readdy.ai/api/search-image?query=professional%20residential%20movers%20carefully%20wrapping%20and%20protecting%20furniture%20inside%20a%20beautiful%20modern%20home%2C%20Minneapolis%20neighborhood%2C%20bright%20interior%2C%20organized%20moving%20boxes%2C%20high%20quality%20commercial%20photography%2C%20warm%20tones%2C%20premium%20aesthetic&width=700&height=500&seq=svc002&orientation=landscape',
    features: ['Complete home moving service', 'Appliance disconnection and reconnection', 'Specialty item handling', 'Furniture assembly and disassembly', 'Premium wrapping and padding', 'Climate-controlled truck options', 'Flexible scheduling including weekends', 'No hidden fees policy'],
    process: [
      { icon: 'ri-phone-line', title: 'Consultation', desc: 'We discuss your home and needs to create a custom move plan.' },
      { icon: 'ri-archive-line', title: 'Packing Day', desc: 'We carefully pack and label all items for safe transport.' },
      { icon: 'ri-truck-line', title: 'Moving Day', desc: 'Professional crew arrives on time and handles every room.' },
      { icon: 'ri-home-4-line', title: 'Delivery', desc: 'We unload and place everything exactly where you want it.' },
      { icon: 'ri-check-double-line', title: 'Final Walk', desc: 'We do a walkthrough to ensure everything is perfect.' },
    ],
    faq: [
      { q: 'How do I prepare for a residential move in Minneapolis?', a: 'Start by decluttering and donating items you no longer need. Label boxes by room. Keep important documents and valuables separate. Confirm elevator or parking reservations at your new address. TrueCare will handle the rest — packing, loading, transport, and setup — so you can focus on your family.' },
      { q: 'Do you move large appliances for residential moves?', a: 'Yes. TrueCare Moving Services handles large appliances including refrigerators, washers, dryers, and stoves. We properly disconnect, secure, transport, and reconnect appliances as needed. Our team uses proper dollies and equipment to protect both the appliance and your floors throughout the process.' },
      { q: 'Are you insured for residential moving in Minnesota?', a: 'Yes, TrueCare Moving Services LLC is fully licensed and insured in the state of Minnesota. We carry liability insurance covering your belongings during the move. We\'re also A+ BBB rated and have served Minneapolis residential customers for 10+ years with zero major damage claims.' },
    ],
    aiBlocks: [
      { icon: 'ri-home-4-line', q: 'What is residential moving service?', a: 'Residential moving service handles the complete relocation of household belongings from one home to another. TrueCare provides professional residential moving throughout Minneapolis including packing, loading, transport, unloading, and furniture placement for apartments, condos, townhomes, and houses of all sizes.' },
      { icon: 'ri-shield-check-line', q: 'Why trust TrueCare for residential moving?', a: 'TrueCare is a licensed, insured, family-owned Minneapolis moving company with 10+ years experience, an A+ BBB rating, and 231 five-star Google reviews. We treat your belongings like our own and offer transparent pricing with no hidden fees, making us the most trusted residential movers in the Twin Cities.' },
    ],
  },
  'apartment-moving': {
    title: 'Apartment Moving Services Minneapolis',
    tagline: 'Expert apartment movers who know Minneapolis buildings inside and out.',
    description: 'Apartment moving demands specialized skill — narrow hallways, elevator logistics, building access rules, and tight parking. TrueCare Moving Services specializes in apartment moves across Minneapolis and the Twin Cities, delivering smooth, careful service every time.',
    heroImage: 'https://readdy.ai/api/search-image?query=professional%20apartment%20movers%20carefully%20navigating%20furniture%20through%20a%20modern%20apartment%20hallway%2C%20Minneapolis%20high%20rise%20building%2C%20organized%20boxes%2C%20elevator%20lobby%2C%20clean%20professional%20setting%2C%20high%20quality%20photography%2C%20premium%20urban%20aesthetic&width=700&height=500&seq=svc003&orientation=landscape',
    features: ['Elevator coordination and scheduling', 'Narrow hallway expertise', 'Building-compliant moving processes', 'Scratch-free floor protection included', 'Portable wardrobe boxes provided', 'Flexible early morning and evening slots', 'Furniture disassembly for tight spaces', 'Studio to luxury penthouse experience'],
    process: [
      { icon: 'ri-building-2-line', title: 'Building Review', desc: 'We review your building\'s access rules and elevator requirements.' },
      { icon: 'ri-calendar-check-line', title: 'Reserve Elevator', desc: 'We help coordinate elevator booking and parking permits.' },
      { icon: 'ri-archive-line', title: 'Pack Smart', desc: 'Strategic packing for apartment corridors and small spaces.' },
      { icon: 'ri-truck-line', title: 'Efficient Move', desc: 'Our crew moves fast and carefully through your building.' },
      { icon: 'ri-home-heart-line', title: 'Settled', desc: 'Furniture in place, boxes organized, nothing damaged.' },
    ],
    faq: [
      { q: 'Can you move furniture from upper floor apartments?', a: 'Absolutely. TrueCare specializes in moving furniture from any floor of a Minneapolis apartment building. We coordinate elevator scheduling, use proper equipment for stairs when needed, and ensure your building\'s floors and walls stay protected throughout the process. No job too complex.' },
      { q: 'Do you provide apartment moving in downtown Minneapolis?', a: 'Yes. We regularly move apartments in downtown Minneapolis, Uptown, Northeast, St. Paul, and throughout the Twin Cities. Our team is familiar with high-rise procedures, building management requirements, and parking logistics in urban areas. We handle all coordination on your behalf.' },
    ],
    aiBlocks: [
      { icon: 'ri-building-2-line', q: 'What is apartment moving service?', a: 'Apartment moving service specializes in relocating belongings within or between apartment buildings. TrueCare Moving Services handles all aspects of Minneapolis apartment moves including elevator logistics, building access compliance, narrow corridor navigation, and careful furniture handling to protect your new home.' },
    ],
  },
  'commercial-moving': {
    title: 'Commercial Moving Services Minneapolis',
    tagline: 'Strategic office and business relocation with minimal downtime.',
    description: 'Business moves require precision planning and fast execution. TrueCare Moving Services provides professional commercial moving throughout Minneapolis and the Twin Cities — relocating offices, retail spaces, and businesses with strategic coordination to minimize disruption and downtime.',
    heroImage: 'https://readdy.ai/api/search-image?query=professional%20commercial%20movers%20organizing%20office%20furniture%20equipment%20in%20modern%20Minneapolis%20office%20space%2C%20organized%20boxes%20labeled%2C%20clean%20business%20environment%2C%20professional%20crew%20in%20uniforms%2C%20premium%20corporate%20aesthetic%2C%20high%20quality%20photography&width=700&height=500&seq=svc004&orientation=landscape',
    features: ['After-hours and weekend moves available', 'IT equipment handling protocols', 'Furniture inventory management', 'Modular and systems furniture', 'Chain of custody documentation', 'Minimal business disruption', 'Same-day business setup', 'Retail and office expertise'],
    process: [
      { icon: 'ri-briefcase-line', title: 'Business Assessment', desc: 'We assess your business space, inventory, and requirements.' },
      { icon: 'ri-map-2-line', title: 'Move Blueprint', desc: 'We create a strategic move plan to minimize downtime.' },
      { icon: 'ri-archive-line', title: 'Prep & Label', desc: 'Systematic packing and labeling of all business assets.' },
      { icon: 'ri-truck-line', title: 'Execute Move', desc: 'Fast, organized execution — often completed overnight.' },
      { icon: 'ri-store-2-line', title: 'Business Ready', desc: 'Your new space is set up and operational on day one.' },
    ],
    faq: [
      { q: 'Can you move our office after business hours in Minneapolis?', a: 'Yes. TrueCare Moving Services specializes in after-hours and weekend commercial moves to minimize your business downtime. We can execute the entire office relocation overnight or over a weekend so your team arrives Monday morning fully operational in the new space.' },
      { q: 'Do you move IT equipment and computers safely?', a: 'Yes. We follow IT equipment handling protocols to safely move computers, servers, monitors, and electronics. We recommend professional IT disconnection and reconnection for complex systems, but our team handles physical transport with the proper padding, anti-static materials, and secure loading techniques.' },
    ],
    aiBlocks: [
      { icon: 'ri-store-2-line', q: 'What is commercial moving service?', a: 'Commercial moving service handles office and business relocations. TrueCare Moving Services provides complete commercial moving throughout Minneapolis — including offices, retail spaces, warehouses, and businesses of all sizes. We specialize in minimizing downtime with strategic planning and after-hours execution.' },
    ],
  },
};

const defaultService = serviceData['local-moving'];

const FORM_URL_SERVICE = FORM_URL;

const ServicePageContent = ({ id }: { id: string }) => {
  const data = serviceData[id] || defaultService;
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [formState, setFormState] = useState({ name: '', phone: '', email: '', serviceType: id, message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: formState.name,
        phone: formState.phone,
        email: formState.email,
        serviceType: formState.serviceType,
        message: formState.message,
      });
      await fetch(FORM_URL_SERVICE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative py-28 bg-hero-gradient overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={data.heroImage} alt={data.title} className="w-full h-full object-cover object-top opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/80 to-navy/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-teal/20 border border-teal/30 rounded-full px-4 py-2 mb-6">
              <div className="w-1.5 h-1.5 bg-teal rounded-full"></div>
              <span className="text-teal text-xs font-body tracking-widest uppercase">Minneapolis Moving Services</span>
            </div>
            <h1 className="font-heading font-800 text-white text-4xl lg:text-5xl mb-5 leading-tight">{data.title}</h1>
            <p className="text-white/70 font-body text-lg leading-relaxed mb-8">{data.tagline}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#quote" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-navy font-heading font-700 px-7 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-file-text-line"></i>Get Free Quote
              </a>
              <a href="tel:+16123674481" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-heading font-600 px-7 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-teal"></i>Call (612) 367-4481
              </a>
            </div>
            <div className="flex flex-wrap gap-4 mt-8">
              {['Licensed & Insured', 'A+ BBB Rated', 'No Hidden Fees'].map((b) => (
                <div key={b} className="flex items-center gap-1.5">
                  <i className="ri-check-line text-teal text-sm"></i>
                  <span className="text-white/60 font-body text-sm">{b}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={`hidden lg:block transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <img src={data.heroImage} alt={data.title} className="w-full h-80 object-cover object-top rounded-2xl" />
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <div className="h-0.5 w-8 bg-teal"></div>
                <span className="text-teal font-body text-xs tracking-widest uppercase">Overview</span>
              </div>
              <h2 className="font-heading font-800 text-navy text-3xl lg:text-4xl mb-5 leading-tight">Trusted Moving Service in Minneapolis</h2>
              <p className="text-charcoal/65 font-body text-base leading-relaxed mb-8">{data.description}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.features.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 py-2.5 px-3 bg-cream rounded-lg">
                    <i className="ri-check-double-line text-teal text-sm shrink-0"></i>
                    <span className="font-body text-sm text-charcoal/70">{f}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Mini Quote Form */}
            <div id="quote" className="bg-navy rounded-2xl p-8">
              <h3 className="font-heading font-700 text-white text-xl mb-2">Get a Free Quote</h3>
              <p className="text-text-muted font-body text-sm mb-6">Fast response. No obligation. No hidden fees.</p>
              {submitted ? (
                <div className="text-center py-12">
                  <i className="ri-check-double-line text-teal text-5xl mb-4 block"></i>
                  <h4 className="font-heading font-700 text-white text-lg mb-2">Quote Request Sent!</h4>
                  <p className="text-text-muted font-body text-sm">We&apos;ll respond within hours. Call (612) 367-4481 for immediate help.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-text-muted font-body text-xs mb-1.5">Name *</label>
                      <input type="text" name="name" required value={formState.name} onChange={(e) => setFormState(p => ({ ...p, name: e.target.value }))} placeholder="Your name" className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-3 py-3 font-body text-sm focus:border-teal focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-text-muted font-body text-xs mb-1.5">Phone *</label>
                      <input type="tel" name="phone" required value={formState.phone} onChange={(e) => setFormState(p => ({ ...p, phone: e.target.value }))} placeholder="(612) 000-0000" className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-3 py-3 font-body text-sm focus:border-teal focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-text-muted font-body text-xs mb-1.5">Email</label>
                    <input type="email" name="email" value={formState.email} onChange={(e) => setFormState(p => ({ ...p, email: e.target.value }))} placeholder="your@email.com" className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-3 py-3 font-body text-sm focus:border-teal focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-text-muted font-body text-xs mb-1.5">Details</label>
                    <textarea name="message" maxLength={500} value={formState.message} onChange={(e) => setFormState(p => ({ ...p, message: e.target.value }))} rows={3} placeholder="Tell us about your move..." className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-3 py-3 font-body text-sm focus:border-teal focus:outline-none resize-none"></textarea>
                    <div className="text-text-muted text-xs text-right mt-1">{formState.message.length}/500</div>
                  </div>
                  <button type="submit" disabled={submitting} className="w-full bg-teal hover:bg-teal-dark text-navy font-heading font-700 py-3.5 rounded-xl transition-all text-sm cursor-pointer whitespace-nowrap disabled:opacity-60">
                    {submitting ? 'Sending...' : 'Request Free Quote'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="h-0.5 w-8 bg-teal"></div>
              <span className="text-teal font-body text-xs tracking-widest uppercase">Our Process</span>
              <div className="h-0.5 w-8 bg-teal"></div>
            </div>
            <h2 className="font-heading font-800 text-navy text-3xl lg:text-4xl">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {data.process.map((step, i) => (
              <div key={i} className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-navy rounded-xl mb-4 relative">
                  <i className={`${step.icon} text-teal text-2xl`}></i>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-teal rounded-lg flex items-center justify-center">
                    <span className="font-heading font-800 text-navy text-xs">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                </div>
                <h3 className="font-heading font-700 text-navy text-sm mb-2">{step.title}</h3>
                <p className="text-charcoal/60 font-body text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Answer Block */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-white text-3xl lg:text-4xl mb-3">About This Service</h2>
            <p className="text-text-muted font-body text-base">Key information about our service for Minneapolis residents.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.aiBlocks.map((block, i) => (
              <div key={i} className="bg-white/5 border border-white/10 hover:border-teal/30 rounded-2xl p-7 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal/15 rounded-xl shrink-0">
                    <i className={`${block.icon} text-teal text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-700 text-white text-base mb-2">{block.q}</h3>
                    <p className="text-text-muted font-body text-sm leading-relaxed">{block.a}</p>
                  </div>
                </div>
              </div>
            ))}
            {/* Local relevance block */}
            <div className="bg-white/5 border border-white/10 hover:border-teal/30 rounded-2xl p-7 transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal/15 rounded-xl shrink-0">
                  <i className="ri-map-pin-line text-teal text-xl"></i>
                </div>
                <div>
                  <h3 className="font-heading font-700 text-white text-base mb-2">Where is this service available?</h3>
                  <p className="text-text-muted font-body text-sm leading-relaxed">TrueCare Moving Services covers Minneapolis and the entire Twin Cities metro area including St. Paul, Bloomington, Plymouth, Maple Grove, Eagan, Eden Prairie, Edina, Coon Rapids, Brooklyn Park, and 15+ additional communities. Call us at (612) 367-4481 to confirm availability in your area.</p>
                </div>
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 hover:border-teal/30 rounded-2xl p-7 transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal/15 rounded-xl shrink-0">
                  <i className="ri-price-tag-3-line text-teal text-xl"></i>
                </div>
                <div>
                  <h3 className="font-heading font-700 text-white text-base mb-2">How is pricing determined?</h3>
                  <p className="text-text-muted font-body text-sm leading-relaxed">TrueCare Moving Services provides transparent, upfront pricing based on move size, distance, services required, and time. We offer hourly and flat-rate options with no hidden fees. All quotes are provided for free with no obligation. Call (612) 367-4481 for a fast estimate.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-800 text-navy text-3xl lg:text-4xl mb-3">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {data.faq.map((f, i) => (
              <div key={i} className={`border rounded-xl overflow-hidden transition-all ${openFaq === i ? 'border-teal/30 bg-teal/5' : 'border-navy/10 bg-cream'}`} itemScope itemType="https://schema.org/Question">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer">
                  <span className="font-heading font-600 text-navy text-sm pr-4" itemProp="name">{f.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 transition-all ${openFaq === i ? 'bg-teal text-navy' : 'bg-navy/5 text-navy/50'}`}>
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-sm`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5" itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                    <p className="text-charcoal/65 font-body text-sm leading-relaxed" itemProp="text">{f.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-navy text-center">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-800 text-white text-4xl mb-4">Ready to Book Your Move?</h2>
          <p className="text-text-muted font-body text-lg mb-8">Licensed. Insured. Family-owned. Transparent pricing. No hidden fees.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="#quote" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-navy font-heading font-700 px-10 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-file-text-line"></i>Get Free Quote
            </a>
            <a href="tel:+16123674481" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-teal text-white hover:text-teal font-heading font-600 px-10 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill"></i>Call (612) 367-4481
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServicesPage = () => {
  const { id } = useParams<{ id: string }>();
  const allServices = [
    { id: 'local-moving', title: 'Local Moving', icon: 'ri-map-pin-2-line', desc: 'Efficient local moves throughout Minneapolis and Twin Cities.' },
    { id: 'residential-moving', title: 'Residential Moving', icon: 'ri-home-4-line', desc: 'Full-service home moving with expert care.' },
    { id: 'apartment-moving', title: 'Apartment Moving', icon: 'ri-building-2-line', desc: 'Specialist apartment movers for Minneapolis buildings.' },
    { id: 'house-moving', title: 'House Moving', icon: 'ri-community-line', desc: 'Complete house-to-house relocation service.' },
    { id: 'commercial-moving', title: 'Commercial Moving', icon: 'ri-store-2-line', desc: 'Strategic office and business relocation.' },
    { id: 'full-service-moving', title: 'Full-Service Moving', icon: 'ri-service-line', desc: 'We handle everything from packing to setup.' },
    { id: 'heavy-item-moving', title: 'Heavy-Item Moving', icon: 'ri-shield-check-line', desc: 'Pianos, safes, gym equipment moved safely.' },
    { id: 'last-minute-moving', title: 'Last-Minute Moving', icon: 'ri-timer-flash-line', desc: 'Rapid-response same-day and next-day moves.' },
    { id: 'loading-unloading', title: 'Loading & Unloading', icon: 'ri-loader-4-line', desc: 'Labor-only loading for rental trucks and pods.' },
  ];

  if (!id) {
    return (
      <div style={{ fontFamily: '"Inter", sans-serif' }}>
        <Header />
        <main>
          <section className="pt-32 pb-20 bg-hero-gradient">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
              <div className="inline-flex items-center gap-2 bg-teal/20 border border-teal/30 rounded-full px-4 py-2 mb-6">
                <span className="text-teal text-xs font-body tracking-widest uppercase">Professional Moving Services</span>
              </div>
              <h1 className="font-heading font-800 text-white text-5xl mb-5">Minneapolis Moving Services</h1>
              <p className="text-white/70 font-body text-xl max-w-2xl mx-auto mb-10">
                Complete moving solutions for homeowners, renters, and businesses throughout the Twin Cities.
              </p>
              <div className="flex justify-center gap-4">
                <Link to="/#quote" className="bg-teal hover:bg-teal-dark text-navy font-heading font-700 px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">Get Free Quote</Link>
                <a href="tel:+16123674481" className="bg-white/10 border border-white/30 text-white font-heading font-600 px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap">Call (612) 367-4481</a>
              </div>
            </div>
          </section>
          <section className="py-20 bg-cream">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allServices.map((svc) => (
                  <Link key={svc.id} to={`/services/${svc.id}`} className="group bg-white rounded-2xl p-7 border border-transparent hover:border-teal/30 transition-all cursor-pointer hover:-translate-y-1">
                    <div className="w-14 h-14 flex items-center justify-center bg-navy/5 group-hover:bg-teal/15 rounded-xl mb-5 transition-colors">
                      <i className={`${svc.icon} text-navy group-hover:text-teal text-2xl transition-colors`}></i>
                    </div>
                    <h3 className="font-heading font-700 text-navy text-lg mb-2">{svc.title}</h3>
                    <p className="text-charcoal/60 font-body text-sm leading-relaxed mb-4">{svc.desc}</p>
                    <div className="flex items-center gap-1.5 text-teal font-heading font-600 text-sm">
                      <span>Learn More</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: '"Inter", sans-serif' }}>
      <Header />
      <main>
        <ServicePageContent id={id} />
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
